import { useState, useEffect, useCallback } from 'react';
import { getSharePointService } from '../services/sharePointService';
import { DepartmentDirector } from '../types/sharepoint';

interface DepartmentDirectorsMap {
  [department: string]: string[]; // Array of director names per department
}

/**
 * Hook to load and manage department directors from SharePoint
 * Provides cached directors and helper functions
 */
export const useDepartmentDirectors = () => {
  const [directors, setDirectors] = useState<DepartmentDirector[]>([]);
  const [directorsMap, setDirectorsMap] = useState<DepartmentDirectorsMap>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadDirectors = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const service = getSharePointService();
      const allDirectors = await service.getDepartmentDirectors();
      
      setDirectors(allDirectors);
      
      // Create a map of department -> director names
      const map: DepartmentDirectorsMap = {};
      allDirectors.forEach(director => {
        if (!map[director.department]) {
          map[director.department] = [];
        }
        map[director.department].push(director.directorName);
      });
      
      setDirectorsMap(map);
    } catch (err) {
      console.error('Error loading department directors:', err);
      setError(err instanceof Error ? err.message : 'Failed to load directors');
      // Keep existing directors on error
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDirectors();
  }, [loadDirectors]);

  /**
   * Get directors for a specific department
   * Returns array of director names (supports multiple)
   */
  const getDirectorsForDepartment = useCallback((department?: string): string[] => {
    if (!department) return [];
    return directorsMap[department] || [];
  }, [directorsMap]);

  /**
   * Get first director for a department (for backward compatibility)
   */
  const getFirstDirector = useCallback((department?: string): string => {
    const directors = getDirectorsForDepartment(department);
    if (directors.length === 0) return 'Not assigned';
    // If multiple, return comma-separated
    return directors.length === 1 ? directors[0] : directors.join(', ');
  }, [getDirectorsForDepartment]);

  /**
   * Get all director objects for a department
   */
  const getDirectorObjects = useCallback((department?: string): DepartmentDirector[] => {
    if (!department) return [];
    return directors.filter(d => d.department === department && d.isActive);
  }, [directors]);

  /**
   * Refresh directors from SharePoint
   */
  const refreshDirectors = useCallback(() => {
    loadDirectors();
  }, [loadDirectors]);

  return {
    directors,
    directorsMap,
    loading,
    error,
    getDirectorsForDepartment,
    getFirstDirector,
    getDirectorObjects,
    refreshDirectors,
  };
};

